/**
 * Service worker registration and update handling.
 *
 * Replaces vite-plugin-pwa's generated registerSW.js, which only did:
 *
 *   navigator.serviceWorker.register('/sw.js', { scope: '/' })
 *
 * That registers the worker but never reloads the page. Combined with
 * skipWaiting + clientsClaim, the NEW worker takes control immediately while
 * the page keeps running the OLD JS bundle it already loaded — so a deploy
 * lands on the server, the worker updates, and the user still sees the
 * previous UI. On an installed PWA that is resumed rather than freshly
 * navigated, that stale state can persist for days.
 *
 * This adds the two missing pieces:
 *
 * 1. Reload once a NEW worker takes control, so updated code is actually
 *    rendered.
 * 2. Check for updates when the app becomes visible again, because an
 *    installed PWA may never fire a `load` event between deploys.
 */

/** Guard against reload loops if controllerchange fires more than once. */
let isReloading = false;

export function registerServiceWorker(): void {
  // Truthiness, not `in`: the property can exist while being undefined (some
  // embedded webviews and test environments), and `in` would pass before
  // dereferencing throws.
  if (typeof navigator === "undefined" || !navigator.serviceWorker) {
    return;
  }

  // Whether this page is already controlled. On a FIRST install the worker
  // claims an uncontrolled page and controllerchange fires once — reloading
  // then would be a pointless refresh on someone's first visit. Only an
  // update, where a controller already existed, warrants a reload.
  const hadController = Boolean(navigator.serviceWorker.controller);

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController || isReloading) return;
    isReloading = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((registration) => {
        // An installed PWA can sit resumed in the background for days without
        // a navigation, so a load-time check alone never fires again. Re-check
        // whenever the user returns to the app.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            void registration.update();
          }
        });
      })
      .catch((error) => {
        // A failed registration must not take the app down — it only means
        // offline support and update detection are unavailable.
        console.error("Service worker registration failed:", error);
      });
  });
}
